import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, PlayCircle } from "lucide-react";
import api from "../lib/api";
import * as C from "../lib/crypto";
import { BeaconMedia } from "../pages/BeaconMedia";

const Card = ({ accent, label, children, testid, action, onPlay }) => (
  <div className="border p-5 sm:p-6 mb-6 bg-[#121212]" style={{ borderColor: accent + "55" }} data-testid={testid} onPlayCapture={onPlay}>
    <div className="flex items-center justify-between gap-3 mb-3">
      <div className="font-body text-[10px] tracking-[0.2em] uppercase" style={{ color: accent }}>{label}</div>
      {action}
    </div>
    {children}
  </div>
);

const Message = ({ text, testid }) => text
  ? <p className="font-body text-base text-zinc-200 leading-relaxed whitespace-pre-wrap break-words" data-testid={testid}>{text}</p>
  : null;

// Shown to members who haven't subscribed yet (and on the public teaser if the creator allows).
export const VisitorWelcome = ({ b, accent, videoSrc, onTrailerPlay }) => {
  const hasVideo = videoSrc || b.welcome_video;
  if (!b.welcome_message && !hasVideo) return null;
  return (
    <Card accent={accent} label="Welcome" testid="beacon-visitor-welcome" onPlay={onTrailerPlay}>
      {videoSrc ? <video src={videoSrc} controls controlsList="nodownload" className="w-full max-h-[420px] rounded mb-4 bg-black" data-testid="beacon-visitor-welcome-video" />
        : b.welcome_video && <BeaconMedia path={b.welcome_video} type="video" className="w-full max-h-[420px] rounded mb-4 bg-black" />}
      <Message text={b.welcome_message} testid="beacon-visitor-welcome-message" />
    </Card>
  );
};

// Private welcome for subscribers, decrypted on-device with the Beacon key.
// Opens fully until seen once (per account), then collapses to a slim bar.
export const SubscriberWelcome = ({ b, handle, accent, encKey, keyPending }) => {
  const sw = b.sub_welcome;
  const [open, setOpen] = useState(!b.welcome_seen);
  const [msg, setMsg] = useState(null);
  // Only count it as seen once it's actually unlocked and on screen.
  useEffect(() => {
    if (encKey && open && !b.is_owner && !b.welcome_seen) api.post(`/beacon/view/${handle}/welcome-seen`).catch(() => {});
  }, [encKey, open, handle, b.is_owner, b.welcome_seen]);
  useEffect(() => {
    if (!encKey || !sw?.msg) return;
    C.decryptText(encKey, sw.msg, sw.msg_iv).then(setMsg).catch(() => setMsg(null));
  }, [encKey, sw?.msg, sw?.msg_iv]);
  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} data-testid="beacon-sub-welcome-collapsed"
      className="w-full flex items-center gap-2 border px-4 py-3 mb-6 bg-[#121212] font-body text-sm text-zinc-300 hover:text-white transition-colors text-left"
      style={{ borderColor: accent + "44" }}>
      <PlayCircle size={16} style={{ color: accent }} className="shrink-0" />
      <span className="flex-1 min-w-0 truncate">A welcome from {b.title || "your creator"}</span>
      <span className="shrink-0 text-xs" style={{ color: accent }}>Watch again</span><ChevronDown size={14} className="shrink-0" />
    </button>
  );
  const locked = !encKey;
  return (
    <Card accent={accent} label="Welcome in, just for subscribers" testid="beacon-sub-welcome"
      action={<button type="button" onClick={() => setOpen(false)} data-testid="beacon-sub-welcome-hide" className="flex items-center gap-1 font-body text-xs text-zinc-500 hover:text-white">Hide <ChevronUp size={13} /></button>}>
      {locked ? <p className="font-body text-sm text-zinc-500" data-testid="beacon-sub-welcome-locked">{keyPending ? "Your welcome unlocks privately once the creator is next active." : "Unlocking your welcome…"}</p> : (
        <>
          {sw.video && <BeaconMedia path={sw.video} type="video" encKey={encKey} iv={sw.video_iv} className="w-full max-h-[420px] rounded mb-4 bg-black" />}
          <Message text={msg} testid="beacon-sub-welcome-message" />
        </>
      )}
    </Card>
  );
};

export const WelcomePreviewToggle = ({ value, onChange, accent }) => (
  <div className="flex items-center gap-2 mb-4 font-body text-xs text-zinc-500 flex-wrap" data-testid="beacon-welcome-preview-toggle">
    <span>Preview welcome as:</span>
    {[["visitor", "Visitor"], ["subscriber", "Subscriber"]].map(([k, l]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`beacon-preview-as-${k}`}
        className={`px-3 py-1 border transition-colors ${value === k ? "text-white" : "border-white/15 hover:bg-white/5"}`}
        style={value === k ? { borderColor: accent, background: accent + "1a" } : undefined}>{l}</button>
    ))}
  </div>
);
